import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../../stores';

/**
 * 忘记密码页面
 */
const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'send' | 'reset'>('send');
  const [contactType, setContactType] = useState<'phone' | 'email'>('phone');
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);

  const { sendVerificationCode, resetPassword, verificationState, resetPasswordState } = useStore();

  // 倒计时效果
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 重置成功后跳转
  useEffect(() => {
    if (resetPasswordState === 'success') {
      navigate('/login');
    }
  }, [resetPasswordState, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSendCode = async () => {
    if (countdown > 0) return;
    
    const contact = contactType === 'phone' ? formData.phone : formData.email;
    if (!contact) {
      setError(`请输入${contactType === 'phone' ? '手机号' : '邮箱'}`);
      return;
    }

    // 验证格式
    if (contactType === 'phone' && !/^1[3-9]\d{9}$/.test(formData.phone)) {
      setError('请输入正确的手机号');
      return;
    }

    if (contactType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('请输入正确的邮箱地址');
      return;
    }

    try {
      setError('');
      const codeData = {
        type: 'reset' as const,
        ...(contactType === 'phone' ? { phone: formData.phone } : { email: formData.email })
      };
      
      await sendVerificationCode(codeData);
      setCodeSent(true);
      setCountdown(60);
      setStep('reset');
    } catch (err: any) {
      setError(err.message || '发送验证码失败');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // 验证密码
    if (formData.newPassword !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setIsLoading(false);
      return;
    }

    // 验证密码强度
    if (formData.newPassword.length < 6) {
      setError('密码至少需要6位字符');
      setIsLoading(false);
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(formData.newPassword)) {
      setError('密码只能包含字母和数字');
      setIsLoading(false);
      return;
    }

    // 验证验证码
    if (!formData.verificationCode) {
      setError('请输入验证码');
      setIsLoading(false);
      return;
    }

    try {
      const resetData = {
        verificationCode: formData.verificationCode,
        newPassword: formData.newPassword,
        ...(contactType === 'phone' ? { phone: formData.phone } : { email: formData.email })
      };
      
      await resetPassword(resetData);
    } catch (err: any) {
      setError(err.message || '重置密码失败');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* 标题 */}
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img 
              src="/images/logo.jpg" 
              alt="AI数理化助手" 
              className="w-12 h-12 mr-3 rounded-lg shadow-lg object-cover"
            />
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              AI数理化助手
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {step === 'send' ? '重置密码' : '设置新密码'}
          </p>
        </div>

        {/* 重置密码表单 */}
        <div className="card p-8">
          {step === 'send' ? (
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {codeSent && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  验证码已发送，请查收
                </div>
              )}

              {/* 联系方式输入 */}
              {contactType === 'phone' ? (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    手机号
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    className="input"
                    placeholder="请输入注册时的手机号"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="text-primary-600 font-medium mr-3">手机号</span>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setContactType('email')}
                      className="ml-3 text-gray-500 hover:text-primary-600 transition-colors"
                    >
                      邮箱
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="input"
                    placeholder="请输入注册时的邮箱地址"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    <button
                      type="button"
                      onClick={() => setContactType('phone')}
                      className="mr-3 text-gray-500 hover:text-primary-600 transition-colors"
                    >
                      手机号
                    </button>
                    <span className="text-gray-300">|</span>
                    <span className="ml-3 text-primary-600 font-medium">邮箱</span>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={handleSendCode}
                disabled={countdown > 0 || verificationState === 'loading'}
                className="btn btn-primary w-full"
              >
                {countdown > 0 ? `${countdown}s后重新发送` : verificationState === 'loading' ? '发送中...' : '发送验证码'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {resetPasswordState === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  密码重置成功，正在跳转到登录页面...
                </div>
              )}

              {/* 验证码输入 */}
              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  验证码
                </label>
                <div className="flex space-x-2">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    required
                    className="input flex-1"
                    placeholder="请输入验证码"
                    value={formData.verificationCode}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={countdown > 0 || verificationState === 'loading'}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium min-w-[100px]"
                  >
                    {countdown > 0 ? `${countdown}s` : verificationState === 'loading' ? '发送中...' : '重新发送'}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  新密码
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  required
                  className="input"
                  placeholder="至少6位，字母或数字组合"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  确认新密码
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className="input"
                  placeholder="请再次输入新密码"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || resetPasswordState === 'loading'}
                className="btn btn-primary w-full"
              >
                {isLoading || resetPasswordState === 'loading' ? '重置中...' : '重置密码'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              想起密码了？{' '}
              <Link to="/login" className="text-primary-600 hover:text-primary-500 font-medium">
                返回登录
              </Link>
            </p>
          </div>
        </div>

        {/* 功能介绍 */}
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <p>重置密码后请妥善保管</p>
          <p>如有问题请联系客服</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;